import { useMemo, useState } from 'react'

import { usePermissions } from '@/hooks/usePermissions'
import { ImPlus } from 'react-icons/im'
import { MdQrCode } from 'react-icons/md'
import { useSelector } from 'react-redux'

import { Box, Flex, HStack, Text, VStack } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'

import { Button, DrawerScrollContainer, SearchInput } from '@/components/ui'
import { TourPlanI } from '@/features/floor-plan'
import { TourPlanListItem } from '@/features/guard-tour-plan'
import { selectMe } from '@/redux/me/meSlice'
import { selectEditMode } from '@/redux/ui/uiSlice'
import { mixpanel } from '@/utils/analytics'

interface TourPlansListIProps {
  facilityId: string
  tourPlans: TourPlanI[]
  onTourPlanClick: (id: string) => void
  onCreateTourPlanClick: () => void
  onDeviceItemHoverIn: (deviceIds: string[]) => void
  onDeviceItemHoverOut: () => void
}

export const TourPlansList = ({
  facilityId,
  tourPlans,
  onTourPlanClick,
  onCreateTourPlanClick,
  onDeviceItemHoverIn,
  onDeviceItemHoverOut,
}: TourPlansListIProps) => {
  const me = useSelector(selectMe)
  const isEditMode = useSelector(selectEditMode)
  const { shouldEnableCreateTourPlan } = usePermissions()
  const [filter, setFilter] = useState('')

  const filteredTouPlans: TourPlanI[] = useMemo(() => {
    return tourPlans?.filter(
      (t) =>
        t?.name?.toLocaleLowerCase()?.includes(filter.toLocaleLowerCase()) ||
        filter === ''
    )
  }, [tourPlans, filter])

  const handleOnClick = (id: string) => {
    onTourPlanClick(id)
  }

  return (
    <>
      <Flex
        align='center'
        borderBottom='1px solid rgba(9, 30, 66, 0.14)'
        direction='column'
        padding='30px'
      >
        <VStack alignItems='start' spacing='10px' w='full'>
          <HStack>
            <MdQrCode color='#44546F' size={26} />
            <Text
              color='#1F2023'
              fontSize='20px'
              fontWeight={700}
              lineHeight='30px'
            >
              Tour Plans
            </Text>
          </HStack>
          <Text
            color='#1F2023'
            fontSize='12px'
            fontWeight={300}
            lineHeight='18px'
            pb='5px'
          >
            {shouldEnableCreateTourPlan
              ? `All active Tour Plans are displayed here. You can also create and configure Tour Plans.`
              : `All active Tour Plans are displayed here.`}
          </Text>
          <Button
            isDisabled={!isEditMode || !shouldEnableCreateTourPlan}
            isFullWidth
            justifyContent='flex-start'
            leftIcon={
              <Box mr='15px'>
                <ImPlus size={12} />
              </Box>
            }
            onClick={() => {
              onCreateTourPlanClick()
              mixpanel.track(
                'Button Press: Facility Tour Plans - Create Tour Plan',
                {
                  facility_id: facilityId,
                  role: me.roleName,
                }
              )
            }}
            variant='affirm'
          >
            Create Tour Plan
          </Button>
        </VStack>
      </Flex>
      <DrawerScrollContainer px='30px' py='30px'>
        <Box pb='26px'>
          <SearchInput
            onChange={(e) => setFilter(e.target.value)}
            placeholder='Filter by name...'
            showIcon={false}
            value={filter}
          />
        </Box>
        <AnimatePresence>
          {filteredTouPlans?.length > 0 ? (
            <Box
              animate={{ opacity: 1 }}
              as={motion.div}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              w='full'
            >
              <VStack align='stretch' spacing='10px'>
                {filteredTouPlans?.map((t) => (
                  <Box
                    animate={{ opacity: 1 }}
                    as={motion.div}
                    exit={{ opacity: 0 }}
                    initial={{ opacity: 0 }}
                    key={t.id}
                    onMouseEnter={() => onDeviceItemHoverIn?.(t.checkpointIds)}
                    onMouseLeave={() => onDeviceItemHoverOut?.()}
                    w='full'
                  >
                    <TourPlanListItem
                      label={t.name}
                      onClick={() => {
                        handleOnClick(t.id)
                        mixpanel.track('Button Press: Tour Plan Details', {
                          facility_id: facilityId,
                          tour_plan_id: t.id,
                          role: me.roleName,
                        })
                      }}
                      subLabel={t.checkpointCount}
                    />
                  </Box>
                ))}
              </VStack>
            </Box>
          ) : (
            <Text
              animate={{ opacity: 1 }}
              as={motion.div}
              color='#9b9b9b'
              exit={{ opacity: 0 }}
              fontSize='14px'
              fontWeight={500}
              initial={{ opacity: 0 }}
              letterSpacing='-0.4px'
              lineHeight='16px'
              my='30px'
              textAlign='center'
            >
              List is empty
            </Text>
          )}
        </AnimatePresence>
      </DrawerScrollContainer>
    </>
  )
}
